import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Pagination } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAuthorPublicationHistory,
  IFetchOrganizationAndJournalsInfo
} from 'src/api/queries';
import PublicationHistoryItemCard from './PublicationHistoryItem';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import { APPLICATION_NAME } from 'src/constants';
const LIMIT = 10;

type PublicationHistoryProps = {
  workspaceId: string;
  userId: string;
  organizationData: IFetchOrganizationAndJournalsInfo;
};

const PublicationHistoryTab = ({
  workspaceId,
  userId,
  organizationData
}: PublicationHistoryProps) => {
  const [page, setPage] = useState<number>(1);
  const offset = (page - 1) * LIMIT;

  const { data: publicationHistory, isLoading } = useQuery({
    queryKey: ['author-publication-history', { workspaceId, userId, page }],
    queryFn: () => fetchAuthorPublicationHistory(workspaceId, userId, offset, LIMIT)
  });

  const totalPages = publicationHistory?.total
    ? Math.ceil(publicationHistory?.total / LIMIT)
    : 0;

  return isLoading ? (
    <SubmissionLoader
      title="Loading Publication History Results"
      subTitle="We are retrieving your search results. This should only take a moment."
      bgColor="neutralSecondaryFill"
    />
  ) : (
    <Flex gap={5} direction="column">
      <Flex flexDirection={'column'}>
        <Text textStyle="headingItem">
          {publicationHistory?.total} Submissions published in{' '}
          {organizationData?.organization?.name}
        </Text>
        <Text>
          Publication history is limited only to submissions hosted on {APPLICATION_NAME}.
        </Text>
      </Flex>
      {publicationHistory && publicationHistory?.items?.length ? (
        <>
          {publicationHistory?.items?.map(
            data => data && <PublicationHistoryItemCard data={data} />
          )}
          {totalPages > 1 && (
            <Flex width={'100%'} justifyContent={'center'} marginTop={4}>
              <Pagination
                ariaLabel="Publication history pagination"
                currentPage={page}
                onPageChange={setPage}
                totalPages={totalPages}
              />
            </Flex>
          )}
        </>
      ) : (
        <NoRecordFound
          title="No Publication History Found"
          subTitle={`This user has not submitted any manuscripts to ${APPLICATION_NAME}. Their publication history will appear here once they do.`}
        />
      )}
    </Flex>
  );
};

export default PublicationHistoryTab;
